import { BasePageModel, basePageToLocal, basePageToServer } from './base';
import { SourceType, ConfirmationMessageType } from '@config/index';
import { TriggerCondition, conditionToLocal, conditionToServer, ListAction } from './response';
import {
  PromptInfo,
  promptInfoToLocal,
  promptInfoToServer
} from './prompt';
import {
  TrainingPhraseConfig,
  trainingPhraseToLocal,
  trainingPhraseToServer
} from './training-phrase';
import { referenceNodeToLocal, referenceNodeToServer } from './parameter';
import { inputParameterToLocal, inputParameterToServer } from './data-query';

export type ConfirmationInfo = BasePageModel & {
  multiValued?: boolean;
  conditionType: SourceType;
  triggerConditions?: TriggerCondition[];
  codeCondition: string;
  codeComment?: string;
  explicitConfirmation: boolean;
  messageType: ConfirmationMessageType;
  prompts: PromptInfo[];
  listAction?: ListAction;
  affirmatives: TrainingPhraseConfig[];
  negatives: TrainingPhraseConfig[];
  masterSelf?: ConfirmationInfo;
};

export function confirmationInfoToLocal(res: any): ConfirmationInfo {
  return {
    ...basePageToLocal(res),
    multiValued: res.multi_valued,
    conditionType: res.condition_type || SourceType.CODE_EXPRESSION,
    triggerConditions: res.trigger_conditions &&
      res.trigger_conditions.map(conditionToLocal),
    codeCondition: res.code_condition,
    codeComment: res.code_comment,
    explicitConfirmation: res.explicit_confirmation,
    messageType: res.message_type || ConfirmationMessageType.SingleValue,
    prompts: (res.prompts || []).map(promptInfoToLocal),
    listAction: res.list_action && {
      type: 'list_action',
      itemSize: res.list_action.item_size,
      header: res.list_action.header,
      footer: res.list_action.footer,
      body: res.list_action.body,
      templateId: res.list_action.template_id,
      sourceType: res.list_action.source_type ||
        (res.list_action.source ? SourceType.CODE_EXPRESSION : SourceType.REGULAR),
      source: res.list_action.source,
      variable: (res.list_action.variable || []).map(referenceNodeToLocal),
      parameters: (res.list_action.parameters || []).map(inputParameterToLocal),
      delimiterField: res.list_action.delimiter_field,
      delimiterEnd: res.list_action.delimiter_end
    },
    affirmatives: (res.affirmatives || []).map(trainingPhraseToLocal),
    negatives: (res.negatives || []).map(trainingPhraseToLocal),
    masterSelf: res.master_self && confirmationInfoToLocal(res.master_self)
  };
}

export function confirmationInfoToServer(data: ConfirmationInfo) {
  const res: any = {
    ...basePageToServer(data),
    id: data.id,
    agent_id: data.agentId,
    owner_id: data.ownerId,
    frame_id: data.frameId,
    owner_type: data.ownerType,
    multi_valued: data.multiValued,
    condition_type: data.conditionType,
    trigger_conditions: data.triggerConditions &&
      data.triggerConditions.map(conditionToServer),
    code_condition: data.codeCondition,
    code_comment: data.codeComment,
    explicit_confirmation: data.explicitConfirmation,
    message_type: data.messageType || ConfirmationMessageType.SingleValue,
    prompts: (data.prompts || []).map(promptInfoToServer),
    list_action: data.listAction && {
      item_size: data.listAction.itemSize,
      header: data.listAction.header,
      footer: data.listAction.footer,
      body: data.listAction.body,
      template_id: data.listAction.templateId,
      source_type: data.listAction.sourceType,
      source: data.listAction.source,
      variable: (data.listAction.variable || []).map(referenceNodeToServer),
      parameters: (data.listAction.parameters || []).map(inputParameterToServer),
      delimiter_field: data.listAction.delimiterField,
      delimiter_end: data.listAction.delimiterEnd
    },
    affirmatives: (data.affirmatives || []).map(trainingPhraseToServer),
    negatives: (data.negatives || []).map(trainingPhraseToServer),
    to_be_deleted: data.toBeDeleted
  };

  if (!res.id || !res.id.length) {
    delete res.id;
  }

  return res;
}
